import React from "react";

type Toast = {
  id: number;
  text: string;
  tone: "ok" | "bad";
  undo?: () => void | Promise<void>;
};
type Notify = (
  text: string,
  opts?: { tone?: Toast["tone"]; undo?: Toast["undo"] },
) => void;

const ToastCtx = React.createContext<Notify | null>(null);

/** Confirms an action happened ("Budget saved") and offers Undo when the
 * caller can reverse it. Announced to screen readers; gone after 5s. */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const next = React.useRef(1);
  const dismiss = React.useCallback(
    (id: number) => setToasts((t) => t.filter((x) => x.id !== id)),
    [],
  );
  const notify = React.useCallback<Notify>(
    (text, opts) => {
      const id = next.current++;
      setToasts((t) => [
        ...t.slice(-2),
        { id, text, tone: opts?.tone ?? "ok", undo: opts?.undo },
      ]);
      window.setTimeout(() => dismiss(id), opts?.undo ? 8000 : 5000);
    },
    [dismiss],
  );
  return (
    <ToastCtx.Provider value={notify}>
      {children}
      <div className="ds-toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`ds-toast ${t.tone}`}>
            <span>{t.text}</span>
            {t.undo && (
              <button
                type="button"
                onClick={() => {
                  dismiss(t.id);
                  Promise.resolve()
                    .then(t.undo)
                    .catch((e: Error) =>
                      notify(`Couldn't undo: ${e.message}`, { tone: "bad" }),
                    );
                }}
              >
                Undo
              </button>
            )}
            <button
              type="button"
              className="ds-toast-x"
              aria-label="Dismiss"
              onClick={() => dismiss(t.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/** No-op outside a provider, so components stay usable in isolation. */
export function useToast(): Notify {
  return React.useContext(ToastCtx) ?? (() => {});
}
